import { useEffect, useMemo, useRef, useState } from "react";
import { parseBannerSlides } from "../helpers/bannerSlides";

const normalizeUrl = (url = "") => {
  const t = String(url || "").trim();
  if (!t) return "";
  return /^https?:\/\//i.test(t) ? t : `https://${t}`;
};

const BannerSlider = ({ value = "", linkUrl = "" }) => {
  const slides = useMemo(
    () => parseBannerSlides(value, linkUrl),
    [value, linkUrl]
  );
  const [index, setIndex] = useState(0);
  const [animated, setAnimated] = useState(true);

  // Change 1 — stable refs for timer control
  const timerRef = useRef(null);
  const slidesLenRef = useRef(slides.length);
  slidesLenRef.current = slides.length;

  const stopTimer = useRef(() => {
    clearInterval(timerRef.current);
    timerRef.current = null;
  });

  const startTimer = useRef(() => {
    stopTimer.current();
    if (slidesLenRef.current <= 1) return;
    timerRef.current = setInterval(() => {
      setAnimated(true);
      setIndex((i) => i + 1);
    }, 4000);
  });

  const resetSlider = useRef(() => {
    stopTimer.current();
    setAnimated(false);
    setIndex(0);
    // Timer restarts inside the !animated effect after rAF completes
  });

  // Reset to slide 0 when the value prop changes
  useEffect(() => {
    resetSlider.current();
  }, [value]);

  // Change 2 — replace old setInterval effect
  useEffect(() => {
    startTimer.current();
    return () => stopTimer.current();
  }, [slides.length]);

  // Change 5 — rAF re-enable + restart timer after snap-back
  useEffect(() => {
    if (!animated) {
      let raf1 = requestAnimationFrame(() => {
        let raf2 = requestAnimationFrame(() => {
          setAnimated(true);
          startTimer.current();
        });
        return () => cancelAnimationFrame(raf2);
      });
      return () => cancelAnimationFrame(raf1);
    }
  }, [animated]);

  // Change 3 — snap-back via setTimeout instead of onTransitionEnd
  useEffect(() => {
    if (index !== slides.length || slides.length === 0) return;
    const t = setTimeout(() => {
      setAnimated(false);
      setIndex(0);
    }, 1300);
    return () => clearTimeout(t);
  }, [index, slides.length]);

  // Change 4 — Page Visibility API: stop drift when hidden, reset on return
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        stopTimer.current();
      } else {
        resetSlider.current();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  if (!slides.length) return null;

  const extendedSlides = slides.length > 1 ? [...slides, slides[0]] : slides;
  const activeDot = index % slides.length;

  // Change 6 — stop and restart timer on manual dot click
  const goToSlide = (i) => {
    stopTimer.current();
    setAnimated(true);
    setIndex(i);
    setTimeout(() => startTimer.current(), 4000);
  };

  return (
    <div style={{ overflow: "hidden", borderRadius: 10, position: "relative" }}>
      <div
        style={{
          display: "flex",
          transform: `translateX(-${index * 100}%)`,
          transition: animated ? "transform 1.2s ease-in-out" : "none",
        }}
        // onTransitionEnd removed — replaced by setTimeout in Change 3
      >
        {extendedSlides.map((slide, i) => {
          const href = normalizeUrl(slide.link);
          const img = (
            <img
              src={slide.image}
              alt={`Slide ${i + 1}`}
              className="customer-banner-image"
            />
          );
          return (
            <div key={i} style={{ flex: "0 0 100%" }}>
              {href ? (
                <a
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  className="customer-banner-link"
                >
                  {img}
                </a>
              ) : (
                img
              )}
            </div>
          );
        })}
      </div>
      {slides.length > 1 && (
        <div className="banner-slider-dots">
          {slides.map((_, i) => (
            <button
              key={i}
              className={`banner-slider-dot${i === activeDot ? " active" : ""}`}
              onClick={() => goToSlide(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default BannerSlider;
